import { apiRequest } from "./client";

/** The worker's oldest live question, shown as its card. */
export interface TaskWorkerQuestion {
  awaiting_input_id: string;
  question: string;
  options: { id: string; label: string }[];
  /** True when it is the only live question, so a composer message answers it. */
  composer_can_answer: boolean;
}

export interface TaskWorkerState {
  provider?: string;
  connection_status?: "connected" | "offline";
  failure_reason?: string | null;
  last_event_at?: string;
  control_unavailable_reason?: string | null;
  /** The user's coding app holds a direct chat: a soft hold, input stays on. */
  control_unavailable_code?: "app_holds_session" | null;
  /** How the user's Claude app shows a direct chat: only once its Denker turn
   *  ends, live, or as a draft waiting for Enter; blank when unknown. */
  app_view?: "after_turn" | "live" | "waiting_enter" | "";
  /** A direct chat in the user's app may wait for an approval only the app can answer. */
  app_waiting_tool?: string | null;
  pending_control_action?: "pause" | "resume" | "instruct" | null;
  execution_id: string | null;
  conversation_id: string | null;
  parent_conversation_id?: string | null;
  status: string;
  execution_status?: string;
  result?: {
    execution_id?: string;
    result_message_id?: string;
    brief_fingerprint?: string;
    accepted_outcome?: "done" | "blocked" | null;
    reviewable?: boolean;
  } | null;
  model: string | null;
  model_source?: "observed" | "unknown";
  execution_mode: "standard" | "deep" | "direct";
  resume_unavailable_reason?: string | null;
  control_error?: string | null;
  can_pause: boolean;
  can_resume: boolean;
  can_instruct: boolean;
  pending_input_count: number;
  pending_question?: TaskWorkerQuestion | null;
  queued_instruction_count: number;
  context_used_tokens?: number | null;
  context_window_max?: number | null;
}

export async function getTaskWorker(projectId: string, taskId: string) {
  const response = await apiRequest<{
    data: { worker?: TaskWorkerState | null };
  }>(`/api/v1/projects/${projectId}/tasks/${taskId}/`, { silent: true });
  return response.data.worker ?? null;
}

export async function controlTaskWorker(
  projectId: string,
  taskId: string,
  action: "pause" | "resume",
  executionId: string,
) {
  const response = await apiRequest<{ data: { worker: TaskWorkerState } }>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/worker-control/`,
    { method: "POST", body: { action, expected_execution_id: executionId } },
  );
  return response.data.worker;
}

/** A direct coding session as exposed on the task serializer. */
export interface DirectSessionInfo {
  provider: string;
  model?: string;
  status: string;
  running_in: string | null;
  open_in_app_url: string | null;
  attach_command: string | null;
  has_pending_approval: boolean;
  pending_approval_id: string | null;
  /** Every pending approval, oldest first. */
  pending_approval_ids: string[];
  has_queued_message: boolean;
}

interface DirectSessionResponse {
  data: { direct_session: DirectSessionInfo | null };
}

export async function sendDirectMessage(
  projectId: string,
  taskId: string,
  message: string,
) {
  const response = await apiRequest<DirectSessionResponse>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/direct-session/send/`,
    { method: "POST", body: { message } },
  );
  return response.data.direct_session;
}

export async function stopDirectTurn(projectId: string, taskId: string) {
  const response = await apiRequest<DirectSessionResponse>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/direct-session/stop/`,
    { method: "POST", body: {} },
  );
  return response.data.direct_session;
}

export async function queueDirectMessage(
  projectId: string,
  taskId: string,
  message: string,
) {
  const response = await apiRequest<DirectSessionResponse>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/direct-session/queue/`,
    { method: "POST", body: { message } },
  );
  return response.data.direct_session;
}

export async function cancelQueuedDirectMessage(
  projectId: string,
  taskId: string,
) {
  const response = await apiRequest<DirectSessionResponse>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/direct-session/queue/`,
    { method: "DELETE" },
  );
  return response.data.direct_session;
}

export async function decideDirectApproval(
  projectId: string,
  taskId: string,
  approvalId: string,
  allow: boolean,
) {
  const response = await apiRequest<DirectSessionResponse>(
    `/api/v1/projects/${projectId}/tasks/${taskId}/approvals/${approvalId}/decision/`,
    { method: "POST", body: { allow } },
  );
  return response.data.direct_session;
}

/** Where the session runs: one of the project's repo folders, or any git
 *  checkout path (a new repo joins the project). */
export interface DispatchDirectSessionBody {
  directory_id?: string;
  repo_path?: string;
  provider: "claude" | "codex" | "opencode";
  installation_id?: string;
  title: string;
  description?: string;
  goal?: string;
}

export async function dispatchDirectSession(
  projectId: string,
  body: DispatchDirectSessionBody,
) {
  const response = await apiRequest<{
    data: {
      session_id: string;
      task_id: string;
      direct_session: DirectSessionInfo | null;
    };
  }>(`/api/v1/projects/${projectId}/direct-sessions/dispatch/`, {
    method: "POST",
    body,
  });
  return response.data;
}
